import React from 'react';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { formatDateToShortMonthDayYearTime } from 'utilities/dateHelpers';
import { mapToStandardColor } from 'constants/colors';
import Tag from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/TagsSection/components/Tag';
import CropIconMapping from 'components/CropIconMapping';
import {
  InfoRow,
  NOT_AVAILABLE,
  usePlanTabContentStyle,
  formatArea,
  PlanDetails
} from './PlanDetailPanel.helpers';

export interface PlanDetailsTabProps {
  plan: PlanDetails;
  cropName?: string;
  cropSeasonNames: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allTags: any;
}

const PlanDetailsTab: React.FC<PlanDetailsTabProps> = ({
  plan,
  cropName,
  cropSeasonNames,
  allTags
}) => {
  const tabContentStyle = usePlanTabContentStyle();

  return (
    <div className="details-tab-container" style={tabContentStyle}>
      <div
        className="px-6 py-8 min-w-0"
        data-testid="plan-detail-tab-panel-details"
        style={{ paddingBottom: 72 }}
      >
        {/* Details use the same two-column layout as Applications/Work Orders. */}
        <div className="details-field-list">
          <div className="details-columns-row">
            <div className="details-left-col">
              <InfoRow label="Planned Area">
                {formatArea(plan?.estimatedAreaValue)}{' '}
                {getAbbreviatedUnit(plan?.estimatedAreaUnit)}
              </InfoRow>
              <InfoRow label="Crop" className="plan-details-crop-row">
                <span className="flex h-5 min-w-0 flex-nowrap items-center gap-1">
                  {cropName && (
                    <span className="flex shrink-0 items-center">
                      <CropIconMapping
                        cropObject={{ name: cropName }}
                        size={18}
                      />
                    </span>
                  )}
                  <span className="truncate">{cropName || NOT_AVAILABLE}</span>
                </span>
              </InfoRow>
              <InfoRow label="Crop Season(s)">
                {cropSeasonNames.length > 0
                  ? cropSeasonNames.join(', ')
                  : NOT_AVAILABLE}
              </InfoRow>
            </div>
            <div className="details-right-col">
              {/* Full record name, in case a long Plan name is truncated at the
                panel's header title. */}
              <InfoRow label="Plan ID">{plan?.name || NOT_AVAILABLE}</InfoRow>
              <InfoRow label="Record Created">
                {plan?.createdDateEpoch && plan.createdDateEpoch > 0
                  ? formatDateToShortMonthDayYearTime(plan.createdDateEpoch)
                  : NOT_AVAILABLE}
              </InfoRow>
              <InfoRow label="Tags">
                {plan?.flagIds?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {plan.flagIds.map(flagId => {
                      const tag = (allTags as
                        | Array<{ id: string; name: string; color: string }>
                        | undefined)?.find(item => item.id === flagId);
                      if (!tag) return null;
                      const colorMapping = mapToStandardColor(tag.color);
                      return (
                        <Tag
                          key={tag.id}
                          name={tag.name}
                          backgroundColor={colorMapping.background}
                          textColor={colorMapping.text}
                          fontSize="12px"
                          paddingRight="8px"
                          paddingLeft="8px"
                        />
                      );
                    })}
                  </div>
                ) : (
                  NOT_AVAILABLE
                )}
              </InfoRow>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanDetailsTab;
